/**
 * Ask API Issue Repository
 * Implements IssueRepository by calling the async /v1/ask endpoint
 * Uses LLM to transform backend KPI data into IssueSummary format
 */

import type {
  IssueDetails,
  IssueSummary,
  IssueSeverity,
  IssueTag,
  IssueOwner,
  EffortEstimate,
  AIConfidence,
  AIConfidenceSignal,
  ContentItemType,
  StepStatus,
  ButtonVariant,
  InsightContentItem,
  BlockerInfo,
  UnblockContact,
  FixGuidanceLink,
  TsgDocument,
  VideoInfo,
  AutomationScript,
  IssueHistoryEntry,
} from '../domain'
import type { IssueRepository, ListIssuesResult } from './IssueRepository'
import { askApiAsyncJson } from '../services/askService'
import { ApiError } from '../services/apiService'
import { WORK_LIST_PROMPT, WORK_LIST_RESPONSE_SCHEMA } from './schemas/issueListSchema'
import {
  WORK_ITEM_DETAILS_PROMPT,
  WORK_ITEM_DETAILS_RESPONSE_SCHEMA,
} from './schemas/issueDetailsSchema'
import type { RawWorkItem, RawWorkListResponse } from '../services/askApiTypes'
import type {
  RawWorkItemDetailsResponse,
  RawInsightContentItem,
  RawBlockerInfo,
  RawUnblockContact,
  RawFixGuidanceLink,
  RawTsgDocument,
  RawVideoInfo,
  RawAutomationScript,
  RawHistoryEntry,
  RawConfidenceSignal,
} from '../services/askApiTypes'

// ─── Validators ─────────────────────────────────────────────────────────────

function isValidSeverity(value: string): value is IssueSeverity {
  return ['critical', 'high', 'medium', 'low'].includes(value)
}

function isValidEffortEstimate(value: string): value is EffortEstimate {
  return ['quick', 'moderate', 'extended'].includes(value)
}

function isValidAIConfidence(value: string): value is AIConfidence {
  return ['high', 'medium', 'low'].includes(value)
}

function isValidContentItemType(value: string): value is ContentItemType {
  return [
    'text', 'bullet', 'link', 'mention', 'actionButton',
    'checklist', 'progressStep', 'referenceLink',
  ].includes(value)
}

function isValidStepStatus(value: string): value is StepStatus {
  return ['pending', 'in_progress', 'completed', 'skipped'].includes(value)
}

function isValidButtonVariant(value: string): value is ButtonVariant {
  return ['primary', 'secondary'].includes(value)
}

function isValidHistoryType(value: string): value is IssueHistoryEntry['type'] {
  return ['status_change', 'assignment', 'comment', 'update', 'escalation'].includes(value)
}

function isValidTsgDocType(value: string): value is TsgDocument['type'] {
  return ['fix_steps', 'general'].includes(value)
}

function isValidVideoType(value: string): value is VideoInfo['type'] {
  return ['ai_generated', 'user_created'].includes(value)
}

function isValidFixGuidanceLinkType(value: string): value is FixGuidanceLink['type'] {
  return ['tsg_fix', 'tsg_general', 'user_video', 'ai_video'].includes(value)
}

// ─── Age / Tag helpers (shared with listIssues) ─────────────────────────────

function calculateAgeDisplay(dueDateStr: string | null): string {
  if (!dueDateStr) {
    return 'Unknown'
  }

  const date = new Date(dueDateStr)
  if (isNaN(date.getTime())) {
    return 'Unknown'
  }

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const absDiffMs = Math.abs(diffMs)

  const minutes = Math.floor(absDiffMs / (1000 * 60))
  const hours = Math.floor(absDiffMs / (1000 * 60 * 60))
  const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24))

  const suffix = diffMs > 0 ? ' overdue' : ' left'

  if (days > 0) {
    return `${days}d${suffix}`
  } else if (hours > 0) {
    return `${hours}h${suffix}`
  } else {
    return `${Math.max(1, minutes)}m${suffix}`
  }
}

function computeTags(dueDateStr: string | null): IssueTag[] {
  if (!dueDateStr) {
    return []
  }

  const due = new Date(dueDateStr)
  if (isNaN(due.getTime())) {
    return []
  }

  const tags: IssueTag[] = []
  const now = new Date()

  if (due < now) {
    tags.push({
      type: 'OVERDUE',
      label: 'Overdue',
      color: 'danger',
      appearance: 'filled',
    })
  }

  return tags
}

// ─── List Issues mapper ─────────────────────────────────────────────────────

function mapWorkItemToIssueSummary(item: RawWorkItem, index: number): IssueSummary {
  const raw = item.id?.trim()
  const id = (raw && raw.toUpperCase() !== 'N/A') ? raw : `unknown-${index}`
  const title = item.title?.trim() || 'Untitled Work Item'
  const severity: IssueSeverity = isValidSeverity(item.severity) ? item.severity : 'medium'
  const category = item.category?.trim() || 'Other'
  const createdAt = item.createdDate?.trim() || ''
  const dueDateStr = item.dueDate?.trim() || null

  return {
    id,
    title,
    severity,
    category,
    tags: computeTags(dueDateStr),
    createdAt,
    ageDisplay: calculateAgeDisplay(dueDateStr),
    isNew: false,
  }
}

// ─── Issue Details mappers ──────────────────────────────────────────────────

function mapContentItems(raw: RawInsightContentItem[] | null | undefined): InsightContentItem[] {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((item) => item.id && item.content)
    .map((item) => {
      const mapped: InsightContentItem = {
        id: item.id,
        type: isValidContentItemType(item.type) ? item.type : 'text',
        content: item.content,
      }
      if (item.href) mapped.href = item.href
      if (item.mentionId) mapped.mentionId = item.mentionId
      if (item.mentionName) mapped.mentionName = item.mentionName
      if (item.buttonVariant && isValidButtonVariant(item.buttonVariant)) {
        mapped.buttonVariant = item.buttonVariant
      }
      if (item.tooltipContent) mapped.tooltipContent = item.tooltipContent
      if (item.checked != null) mapped.checked = item.checked
      if (item.stepStatus && isValidStepStatus(item.stepStatus)) {
        mapped.stepStatus = item.stepStatus
      }
      if (item.stepNumber != null) mapped.stepNumber = item.stepNumber
      return mapped
    })
}

function mapBlockers(raw: RawBlockerInfo[] | null | undefined): BlockerInfo[] {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((b) => b.description)
    .map((b) => ({
      description: b.description,
      duration: b.duration || 'Unknown',
    }))
}

function mapUnblockers(raw: RawUnblockContact[] | null | undefined): UnblockContact[] {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((u) => u.id && u.displayName)
    .map((u) => {
      const mapped: UnblockContact = { id: u.id, displayName: u.displayName }
      if (u.role) mapped.role = u.role
      return mapped
    })
}

function mapFixGuidanceLinks(raw: RawFixGuidanceLink[] | null | undefined): FixGuidanceLink[] {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((l) => l.id && l.label && l.url)
    .map((l) => ({
      id: l.id,
      label: l.label,
      url: l.url,
      type: isValidFixGuidanceLinkType(l.type) ? l.type : 'tsg_general',
    }))
}

function mapTsgDocuments(
  raw: RawTsgDocument[] | null | undefined,
  tsgResearch: string | null | undefined,
): TsgDocument[] {
  const docs: TsgDocument[] = []

  if (raw && Array.isArray(raw)) {
    for (const d of raw) {
      if (!d.id || !d.title || !d.url) continue
      const mapped: TsgDocument = {
        id: d.id,
        title: d.title,
        url: d.url,
        type: isValidTsgDocType(d.type) ? d.type : 'general',
      }
      if (d.content) mapped.content = d.content
      docs.push(mapped)
    }
  }

  // Append synthetic TSG document from API-appended tsgResearch markdown
  if (tsgResearch) {
    docs.push({
      id: 'tsg-research',
      title: 'TSG Research',
      url: '',
      content: tsgResearch,
      type: 'general',
    })
  }

  return docs
}

function mapVideos(raw: RawVideoInfo[] | null | undefined): VideoInfo[] {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((v) => v.id && v.title && v.url)
    .map((v) => {
      const mapped: VideoInfo = {
        id: v.id,
        title: v.title,
        url: v.url,
        duration: v.duration || '0:00',
        type: isValidVideoType(v.type) ? v.type : 'user_created',
      }
      if (v.thumbnailUrl) mapped.thumbnailUrl = v.thumbnailUrl
      if (v.summary) mapped.summary = v.summary
      return mapped
    })
}

function mapAutomationScript(raw: RawAutomationScript | null | undefined): AutomationScript | undefined {
  if (!raw || !raw.id || !raw.title || !raw.content) return undefined
  const mapped: AutomationScript = {
    id: raw.id,
    title: raw.title,
    content: raw.content,
    canLaunch: raw.canLaunch ?? false,
  }
  if (raw.launchDisabledReason) mapped.launchDisabledReason = raw.launchDisabledReason
  return mapped
}

function mapHistory(raw: RawHistoryEntry[] | null | undefined): IssueHistoryEntry[] {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((h) => h.id && h.timestamp && h.description)
    .map((h) => {
      const mapped: IssueHistoryEntry = {
        id: h.id,
        timestamp: h.timestamp,
        type: isValidHistoryType(h.type) ? h.type : 'update',
        actor: {
          id: h.actorId || 'unknown',
          displayName: h.actorDisplayName || 'Unknown',
        },
        description: h.description,
      }
      if (h.details) mapped.details = h.details
      return mapped
    })
}

function mapConfidenceSignals(raw: RawConfidenceSignal[] | null | undefined): AIConfidenceSignal[] | undefined {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return undefined
  return raw
    .filter((s) => s.label)
    .map((s) => ({
      label: s.label,
      isPositive: s.isPositive ?? true,
    }))
}

function mapOwner(raw: { id: string; displayName: string; email?: string | null } | null | undefined): IssueOwner | undefined {
  if (!raw || !raw.id || !raw.displayName) return undefined
  const owner: IssueOwner = { id: raw.id, displayName: raw.displayName }
  if (raw.email) owner.email = raw.email
  return owner
}

function mapRawToIssueDetails(raw: RawWorkItemDetailsResponse): IssueDetails {
  const id = raw.id?.trim() || 'unknown'
  const title = raw.title?.trim() || 'Untitled Work Item'
  const severity: IssueSeverity = isValidSeverity(raw.severity) ? raw.severity : 'medium'
  const category = raw.category?.trim() || 'Other'
  const createdAt = raw.createdDate?.trim() || ''
  const dueDateStr = raw.dueDate?.trim() || null

  const details: IssueDetails = {
    // IssueSummary fields
    id,
    title,
    severity,
    category,
    tags: computeTags(dueDateStr),
    createdAt,
    ageDisplay: calculateAgeDisplay(dueDateStr),
    isNew: false,

    // IssueDetails-specific fields
    description: raw.description?.trim() || '',
    updatedAt: raw.updatedDate?.trim() || '',
    insightSections: [],
    actions: [],
  }

  // Owner
  const owner = mapOwner(raw.owner)
  if (owner) details.owner = owner

  // Overview tab
  const recentUpdates = mapContentItems(raw.recentUpdates)
  if (recentUpdates.length > 0) details.recentUpdates = recentUpdates

  const nextSteps = mapContentItems(raw.nextSteps)
  if (nextSteps.length > 0) details.nextSteps = nextSteps

  const blockers = mapBlockers(raw.blockers)
  if (blockers.length > 0) details.blockers = blockers

  const unblockers = mapUnblockers(raw.unblockers)
  if (unblockers.length > 0) details.unblockers = unblockers

  const fixGuidanceLinks = mapFixGuidanceLinks(raw.fixGuidanceLinks)
  if (fixGuidanceLinks.length > 0) details.fixGuidanceLinks = fixGuidanceLinks

  // Fix Guide tab (tsgResearch is appended by API post-LLM)
  const tsgDocuments = mapTsgDocuments(raw.tsgDocuments, raw.tsgResearch)
  if (tsgDocuments.length > 0) details.tsgDocuments = tsgDocuments

  // Videos tab
  const videos = mapVideos(raw.videos)
  if (videos.length > 0) details.videos = videos

  // Automation tab
  const automationScript = mapAutomationScript(raw.automationScript)
  if (automationScript) details.automationScript = automationScript

  // History tab
  const history = mapHistory(raw.history)
  if (history.length > 0) details.history = history

  // Status chips
  if (raw.effortEstimate && isValidEffortEstimate(raw.effortEstimate)) {
    details.effortEstimate = raw.effortEstimate
  }
  if (raw.aiConfidence && isValidAIConfidence(raw.aiConfidence)) {
    details.aiConfidence = raw.aiConfidence
  }
  const confidenceSignals = mapConfidenceSignals(raw.confidenceSignals)
  if (confidenceSignals) details.confidenceSignals = confidenceSignals

  if (raw.blockedDuration) details.blockedDuration = raw.blockedDuration

  // Metadata
  if (raw.sourceSystem) details.sourceSystem = raw.sourceSystem
  if (raw.externalId) details.externalId = raw.externalId
  if (raw.relatedIssueIds && Array.isArray(raw.relatedIssueIds) && raw.relatedIssueIds.length > 0) {
    details.relatedIssueIds = raw.relatedIssueIds
  }

  return details
}

// ─── Repository ─────────────────────────────────────────────────────────────

export class AskApiIssueRepository implements IssueRepository {
  async listIssues(): Promise<ListIssuesResult> {
    const request = {
      prompt: WORK_LIST_PROMPT,
      responseFormat: 'json' as const,
      responseSchema: JSON.stringify(WORK_LIST_RESPONSE_SCHEMA),
      workItemId: null,
    }

    const rawResponse = await askApiAsyncJson<RawWorkListResponse>(request)

    // Contract validation: workItems must exist and be an array
    if (!rawResponse.workItems || !Array.isArray(rawResponse.workItems)) {
      throw new Error('Ask API response missing workItems array — contract error')
    }

    const issues: IssueSummary[] = rawResponse.workItems.map(mapWorkItemToIssueSummary)

    return {
      issues,
      totalCount: issues.length,
    }
  }

  async getIssueDetails(id: string): Promise<IssueDetails | null> {
    const request = {
      prompt: WORK_ITEM_DETAILS_PROMPT,
      responseFormat: 'json' as const,
      responseSchema: JSON.stringify(WORK_ITEM_DETAILS_RESPONSE_SCHEMA),
      workItemId: id,
    }

    try {
      const rawResponse = await askApiAsyncJson<RawWorkItemDetailsResponse>(request)
      return mapRawToIssueDetails(rawResponse)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return null
      }
      throw err
    }
  }
}

// Default singleton instance
export const askApiIssueRepository = new AskApiIssueRepository()
